import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageStore from '../../store/LanguageStore';
import GameService from '../../services/GameService';
import { observer } from 'mobx-react-lite';
import './game-invite.scss';

const GameInvite = observer(({ inviteLink, sessionId, onClose, onPlayerJoined }) => {
    const t = LanguageStore.t;
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [isWaiting, setIsWaiting] = useState(true);
    const [playerJoined, setPlayerJoined] = useState(false);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(inviteLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Подключение к Mercure для получения обновлений в реальном времени
    useEffect(() => {
        if (!sessionId) return;

        const connectMercure = async () => {
            const mercureUrl = process.env.REACT_APP_MERCURE_URL || 'http://localhost:3001/.well-known/mercure';
            const url = new URL(mercureUrl);
            url.searchParams.append('topic', `game-session/${sessionId}`);
            
            // Получаем JWT токен для Mercure
            // Mercure принимает токен через query параметр authorization без префикса "Bearer"
            try {
                const token = await GameService.getMercureToken();
                url.searchParams.append('authorization', token);
            } catch (error) {
                console.error('Failed to get Mercure token:', error);
                // Продолжаем без токена, возможно Mercure разрешит анонимный доступ
            }

            const eventSource = new EventSource(url);

            eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'player_joined' && data.isFull) {
                    setPlayerJoined(true);
                    setIsWaiting(false);
                    eventSource.close();
                    
                    // Переходим к игре через 1 секунду
                    setTimeout(() => {
                        if (onPlayerJoined) {
                            onPlayerJoined();
                        } else {
                            navigate(`/game/${sessionId}`);
                        }
                    }, 500);
                }
            } catch (error) {
                console.error('Error parsing Mercure message:', error);
            }
        };

            eventSource.onerror = (error) => {
                console.error('Mercure connection error:', error);
                // Fallback на polling при ошибке
                const checkInterval = setInterval(async () => {
                try {
                    const result = await GameService.getSession(sessionId);
                    if (result.success && result.session.isFull) {
                        setPlayerJoined(true);
                        setIsWaiting(false);
                        clearInterval(checkInterval);
                        eventSource.close();
                        
                        setTimeout(() => {
                            if (onPlayerJoined) {
                                onPlayerJoined();
                            } else {
                                navigate(`/game/${sessionId}`);
                            }
                        }, 1000);
                    }
                } catch (err) {
                    console.error('[GameInvite] Error checking session:', err);
                    console.error('[GameInvite] Error details:', {
                        message: err.message,
                        response: err.response?.data,
                        status: err.response?.status
                    });
                }
            }, 2000);

                return () => {
                    clearInterval(checkInterval);
                    eventSource.close();
                };
            };
        };

        connectMercure();

        return () => {};
    }, [sessionId, navigate, onPlayerJoined]);

    return (
        <div className="game-invite">
            <div className="game-invite__modal">
                <div className="game-invite__content">
                    <button 
                        className="game-invite__close-button"
                        onClick={onClose}
                        aria-label={t('close')}
                    >
                        ×
                    </button>
                    <h2 className="game-invite__title">{t('inviteFriend')}</h2>
                    <p className="game-invite__description">{t('inviteFriendDescription')}</p>
                    
                    <div className="game-invite__link-container">
                        <input 
                            type="text" 
                            value={inviteLink} 
                            readOnly 
                            className="game-invite__link-input"
                        />
                        <button 
                            className={`game-invite__copy-button ${copied ? 'game-invite__copy-button--copied' : ''}`}
                            onClick={handleCopyLink}
                        >
                            {copied ? '✓' : t('copyLink')}
                        </button>
                    </div>

                    {copied && (
                        <div className="game-invite__copied-message">
                            {t('linkCopied')}
                        </div>
                    )}

                    {!playerJoined ? (
                        <div className="game-invite__waiting">
                            <div className="game-invite__spinner"></div>
                            <p className="game-invite__waiting-text">{t('waitingForPlayer')}</p>
                        </div>
                    ) : (
                        <div className="game-invite__joined">
                            <p className="game-invite__joined-text">{t('playerJoined')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default GameInvite;

